import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import {
    Search, Upload, Users, Building2, UserPlus, Trash2, ArrowRightCircle,
    FileSpreadsheet, Filter, CheckCircle2, Clock, MoreHorizontal, Sparkles, History,
    AlertTriangle, RotateCcw, TrendingUp, TrendingDown, Target, ArrowRight, BarChart3,
    Globe, ChevronDown, ChevronUp, X
} from 'lucide-react';
import AssignmentSuggester from '../components/AssignmentSuggester';
import EnrichmentModal from '../components/EnrichmentModal';
import SortableHeader, { useTableSort, useColumnFilters, usePagination, TablePagination } from '../components/table/SortableHeader';
import StagingTable from '../components/staging/StagingTable';
import StagingUploadModal from '../components/staging/StagingUploadModal';
import StagingImportTabs from '../components/staging/StagingImportTabs';
import StagingDashboard from '../components/staging/StagingDashboard';
import StagingCommitModal from '../components/staging/StagingCommitModal';
import StagingRepBalancer from '../components/staging/StagingRepBalancer';
import ImportWizard from '../components/ImportWizard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ListManagement() {
    const { getAuthHeader } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [accounts, setAccounts] = useState([]);
    const [salesRepStats, setSalesRepStats] = useState([]);
    const [filters, setFilters] = useState({ regions: [], countries: [], states: [] });
    
    // Filter state
    const [search, setSearch] = useState('');
    const [regionFilter, setRegionFilter] = useState('');
    const [countryFilter, setCountryFilter] = useState('');
    const [stateFilter, setStateFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    
    // Selection state
    const [selectedIds, setSelectedIds] = useState([]);
    
    // Modal state
    const [showImportModal, setShowImportModal] = useState(false);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [importListName, setImportListName] = useState('');
    const [importing, setImporting] = useState(false);
    const [assignToUserId, setAssignToUserId] = useState('');
    const [enrollCampaignId, setEnrollCampaignId] = useState('');
    const [assigning, setAssigning] = useState(false);
    const [campaigns, setCampaigns] = useState([]);

    // Import preview state
    const [importStep, setImportStep] = useState('upload'); // 'upload', 'preview'
    const [previewData, setPreviewData] = useState(null);
    const [previewing, setPreviewing] = useState(false);
    const [previewSearch, setPreviewSearch] = useState('');

    // AI suggestion step before manual assign
    const [showAISuggestions, setShowAISuggestions] = useState(false);

    // Bulk delete
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [deleting, setDeleting] = useState(false);
    const [restoring, setRestoring] = useState(false);

    // Enrichment modal
    const [showEnrichModal, setShowEnrichModal] = useState(false);

    // Import history
    const [importHistory, setImportHistory] = useState([]);
    const [activeTab, setActiveTab] = useState('staging'); // 'staging', 'targets', 'history', 'deleted'

    // Staging area state
    const [showStagingUpload, setShowStagingUpload] = useState(false);
    const [stagingImports, setStagingImports] = useState([]);
    const [activeStagingImport, setActiveStagingImport] = useState('');
    const [stagingSelectedIds, setStagingSelectedIds] = useState([]);
    const [stagingDashboard, setStagingDashboard] = useState(null);
    const [stagingDashboardLoading, setStagingDashboardLoading] = useState(true);

    // Staging commit
    const [showCommitModal, setShowCommitModal] = useState(false);
    const [commitPreview, setCommitPreview] = useState(null);
    const [committing, setCommitting] = useState(false);

    // Staging geographic filters
    const [stagingGeoFilters, setStagingGeoFilters] = useState({ region: '', country: '', state: '' });
    const [activeRepFilter, setActiveRepFilter] = useState('');

    // Staging view mode: 'staging' (default table) | 'pending-companies' | 'pending-contacts' | 'crm-companies' | 'crm-contacts' | 'unassigned'
    const [stagingView, setStagingView] = useState('staging');

    // CRM data for card-click views
    const [crmCompanies, setCrmCompanies] = useState({ data: [], total: 0 });
    const [crmContacts, setCrmContacts] = useState({ data: [], total: 0 });
    const [pendingCompanies, setPendingCompanies] = useState({ data: [], total: 0 });
    const [viewLoading, setViewLoading] = useState(false);
    const [viewSearch, setViewSearch] = useState('');

    // Import wizard modal
    const [showImportWizard, setShowImportWizard] = useState(false);

    // Dashboard metrics
    const [dashboard, setDashboard] = useState(null);
    const [dashboardLoading, setDashboardLoading] = useState(true);
    const [activeMetricFilter, setActiveMetricFilter] = useState(null); // 'total', 'target', 'assigned', 'converted', 'unassigned'
    const [showBreakdown, setShowBreakdown] = useState(null); // 'region', 'industry'

    // Load target accounts data — only when targets/deleted tab is active
    const loadData = useCallback(async () => {
        if (activeTab !== 'targets' && activeTab !== 'deleted') return;
        try {
            const isDeleted = activeTab === 'deleted';
            const [accountsRes, statsRes, filtersRes] = await Promise.all([
                axios.get(`${API}/admin/target-accounts`, {
                    headers: getAuthHeader(),
                    params: {
                        search: isDeleted ? '' : search,
                        region: isDeleted ? '' : regionFilter,
                        country: isDeleted ? '' : countryFilter,
                        state: isDeleted ? '' : stateFilter,
                        status: isDeleted ? '' : statusFilter,
                        include_deleted: isDeleted,
                        page_size: 10000
                    }
                }),
                axios.get(`${API}/admin/target-accounts/stats`, { headers: getAuthHeader() }),
                axios.get(`${API}/admin/target-accounts/filters`, { headers: getAuthHeader() })
            ]);
            setAccounts(Array.isArray(accountsRes.data) ? accountsRes.data : accountsRes.data.data || []);
            setSalesRepStats(statsRes.data);
            setFilters(filtersRes.data);
        } catch (err) {
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    }, [getAuthHeader, search, regionFilter, countryFilter, stateFilter, statusFilter, activeTab]);

    useEffect(() => { loadData(); }, [loadData]);

    // Load sales rep stats on mount for staging (lightweight call)
    useEffect(() => {
        const loadStats = async () => {
            try {
                const res = await axios.get(`${API}/admin/target-accounts/stats`, { headers: getAuthHeader() });
                setSalesRepStats(res.data);
            } catch { /* ignore */ }
            finally { setLoading(false); }
        };
        loadStats();
    }, [getAuthHeader]);

    // Column filtering + sorting
    const LIST_COLUMNS = [
        { key: 'company_name', label: 'Company' },
        { key: 'industry', label: 'Industry' },
        { key: 'employees', label: 'Employees' },
        { key: 'investor', label: 'Investor' },
        { key: 'investor_type', label: 'Investor Type' },
        { key: 'city', label: 'City' },
        { key: 'state_province', label: 'State' },
        { key: 'country', label: 'Country' },
        { key: 'region', label: 'Region' },
        { key: 'contacts_count', label: 'Contacts' },
        { key: 'status', label: 'Status' },
        { key: 'assigned_to_name', label: 'Assigned To' },
    ];
    const { columnFilters, setColumnFilter, filteredData: columnFilteredAccounts } = useColumnFilters(accounts);
    const { sortColumn, sortDirection, handleSort, sortedData: sortedAccounts } = useTableSort(
        columnFilteredAccounts,
        { numericKeys: ['employees', 'contacts_count'] }
    );
    const { page, pageSize, setPage, setPageSize, pageData: pagedAccounts, totalPages, total: totalAccounts } = usePagination(sortedAccounts, 100);

    // Load dashboard metrics
    const loadDashboard = useCallback(async () => {
        if (activeTab === 'staging') { setDashboardLoading(false); return; }
        try {
            const res = await axios.get(`${API}/admin/target-accounts/dashboard`, { headers: getAuthHeader() });
            setDashboard(res.data);
        } catch (err) {
            console.error('Failed to load dashboard metrics');
        } finally {
            setDashboardLoading(false);
        }
    }, [getAuthHeader, activeTab]);

    useEffect(() => { loadDashboard(); }, [loadDashboard]);

    // ── Staging area loaders ──
    const loadStagingImports = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/staging/imports`, { headers: getAuthHeader() });
            setStagingImports(Array.isArray(res.data) ? res.data : []);
        } catch { /* ignore */ }
    }, [getAuthHeader]);

    const loadStagingDashboard = useCallback(async () => {
        try {
            setStagingDashboardLoading(true);
            const params = {};
            if (activeStagingImport) params.import_id = activeStagingImport;
            if (stagingGeoFilters.region) params.region = stagingGeoFilters.region;
            if (stagingGeoFilters.country) params.company_country = stagingGeoFilters.country;
            if (stagingGeoFilters.state) params.company_state = stagingGeoFilters.state;
            const res = await axios.get(`${API}/staging/dashboard`, { headers: getAuthHeader(), params });
            setStagingDashboard(res.data);
        } catch { console.error('Failed to load staging dashboard'); }
        finally { setStagingDashboardLoading(false); }
    }, [getAuthHeader, activeStagingImport, stagingGeoFilters]);

    useEffect(() => { loadStagingImports(); loadStagingDashboard(); }, [loadStagingImports, loadStagingDashboard]);

    // Load data for card-click views
    const loadViewData = useCallback(async (view) => {
        if (!view || view === 'staging') return;
        setViewLoading(true);
        try {
            if (view === 'crm-companies') {
                const res = await axios.get(`${API}/accounts`, {
                    headers: getAuthHeader(),
                    params: { page_size: 200, search: viewSearch }
                });
                setCrmCompanies({ data: res.data?.data || [], total: res.data?.total || 0 });
            } else if (view === 'crm-contacts') {
                const res = await axios.get(`${API}/contacts`, {
                    headers: getAuthHeader(),
                    params: { page_size: 200, search: viewSearch }
                });
                setCrmContacts({ data: res.data?.data || [], total: res.data?.total || 0 });
            } else if (view === 'pending-companies') {
                const params = { page_size: 200 };
                if (viewSearch) params.search = viewSearch;
                if (stagingGeoFilters.region) params.region = stagingGeoFilters.region;
                if (stagingGeoFilters.country) params.company_country = stagingGeoFilters.country;
                if (stagingGeoFilters.state) params.company_state = stagingGeoFilters.state;
                const res = await axios.get(`${API}/staging/pending-companies`, {
                    headers: getAuthHeader(), params
                });
                setPendingCompanies({ data: res.data?.data || [], total: res.data?.total || 0 });
            }
            // 'pending-contacts' and 'unassigned' use the StagingTable with filters
        } catch (err) {
            toast.error('Failed to load data');
        } finally {
            setViewLoading(false);
        }
    }, [getAuthHeader, viewSearch, stagingGeoFilters]);

    useEffect(() => { loadViewData(stagingView); }, [stagingView, loadViewData]);

    const handleCardClick = (view) => {
        setStagingView(prev => prev === view ? 'staging' : view);
        setViewSearch('');
    };

    const handleDeleteStagingImport = async (importId) => {
        if (!window.confirm('Discard this import and all its staged records?')) return;
        try {
            await axios.delete(`${API}/staging/imports/${importId}`, { headers: getAuthHeader() });
            toast.success('Import discarded');
            if (activeStagingImport === importId) setActiveStagingImport('');
            loadStagingImports();
            loadStagingDashboard();
        } catch { toast.error('Failed to discard import'); }
    };

    const handleCommitPreview = async () => {
        try {
            const params = {};
            // If specific records are selected, preview only those
            if (stagingSelectedIds.length > 0) {
                params.record_ids = stagingSelectedIds.join(',');
            } else if (activeStagingImport) {
                params.import_id = activeStagingImport;
            }
            const res = await axios.get(`${API}/staging/commit-preview`, { headers: getAuthHeader(), params });
            setCommitPreview(res.data);
            setShowCommitModal(true);
        } catch (err) { toast.error('Failed to load commit preview'); }
    };

    // Turn a commit response into a one-line summary that actually tells
    // the rep why "Contacts = 0" happened (dedup vs validation vs no-name).
    // Without this the old toast just said "0 contacts created" with no
    // cue that every row was being skipped as a duplicate.
    const summariseCommitResult = (d) => {
        const parts = [];
        if (d.accounts_created) parts.push(`${d.accounts_created} new ${d.accounts_created === 1 ? 'company' : 'companies'}`);
        if (d.accounts_enriched) parts.push(`${d.accounts_enriched} enriched`);
        if (d.accounts_existing && !d.accounts_enriched) parts.push(`${d.accounts_existing} already in CRM`);
        if (d.contacts_created) parts.push(`${d.contacts_created} ${d.contacts_created === 1 ? 'contact' : 'contacts'}`);
        if (d.contacts_enriched) parts.push(`${d.contacts_enriched} ${d.contacts_enriched === 1 ? 'contact updated' : 'contacts updated'}`);
        const skipBits = [];
        if (d.contacts_skipped_dedup) skipBits.push(`${d.contacts_skipped_dedup} duplicate`);
        if (d.contacts_skipped_no_name) skipBits.push(`${d.contacts_skipped_no_name} no name`);
        if (d.contacts_failed) skipBits.push(`${d.contacts_failed} failed`);
        if (skipBits.length) parts.push(`(skipped: ${skipBits.join(', ')})`);
        return parts.length ? `Committed: ${parts.join(' · ')}` : 'Committed (nothing changed)';
    };

    const handleCommit = async () => {
        setCommitting(true);
        try {
            // Priority: selected records > active import > all imports
            if (stagingSelectedIds.length > 0) {
                // Commit only selected records
                const res = await axios.post(`${API}/staging/commit`, { record_ids: stagingSelectedIds }, { headers: getAuthHeader() });
                const msg = summariseCommitResult(res.data);
                (res.data.contacts_failed ? toast.error : toast.success)(msg);
            } else if (activeStagingImport) {
                // Commit all records in the active import
                const res = await axios.post(`${API}/staging/commit`, { import_id: activeStagingImport }, { headers: getAuthHeader() });
                const msg = summariseCommitResult(res.data);
                (res.data.contacts_failed ? toast.error : toast.success)(msg);
            } else {
                // Commit all active imports
                for (const imp of stagingImports) {
                    await axios.post(`${API}/staging/commit`, { import_id: imp.import_id }, { headers: getAuthHeader() });
                }
                toast.success('All staging data committed to CRM');
            }
            setShowCommitModal(false);
            setCommitPreview(null);
            loadStagingImports();
            loadStagingDashboard();
            loadDashboard();
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Commit failed');
        } finally { setCommitting(false); }
    };

    // Handle metric card click — filters the table below
    const handleMetricClick = (metricKey) => {
        if (activeMetricFilter === metricKey) {
            // Deselect: clear filter
            setActiveMetricFilter(null);
            setStatusFilter('');
        } else {
            setActiveMetricFilter(metricKey);
            setActiveTab('targets');
            if (metricKey === 'total') {
                setStatusFilter('');
            } else if (metricKey === 'unassigned') {
                setStatusFilter('target');
            } else {
                setStatusFilter(metricKey);
            }
        }
    };

    // Load import history from staging imports
    const loadImportHistory = async () => {
        try {
            const res = await axios.get(`${API}/staging/imports`, { headers: getAuthHeader() });
            console.log('Import history API response:', res.data);
            // Show all imports in history (both active and committed)
            const allImports = Array.isArray(res.data) ? res.data : [];
            console.log('All imports for history:', allImports);
            setImportHistory(allImports);
        } catch (err) {
            console.error('Failed to load import history:', err);
        }
    };

    // Load campaigns for enrollment option
    useEffect(() => {
        const loadCampaigns = async () => {
            try {
                const res = await axios.get(`${API}/campaigns`, { headers: getAuthHeader() });
                setCampaigns(Array.isArray(res.data) ? res.data : (res.data?.data || []));
            } catch (err) {
                console.error('Failed to load campaigns');
            }
        };
        loadCampaigns();
    }, [getAuthHeader]);

    // Selection handlers
    const toggleSelectAll = () => {
        if (selectedIds.length === accounts.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(accounts.map(a => a.target_account_id));
        }
    };

    const toggleSelect = (id) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    // Import preview handler — step 1: parse CSV and show preview
    const handlePreview = async () => {
        if (!importFile) {
            toast.error('Please select a CSV file');
            return;
        }
        setPreviewing(true);
        try {
            const formData = new FormData();
            formData.append('file', importFile);
            const res = await axios.post(`${API}/admin/target-accounts/preview`, formData, {
                headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
            });
            setPreviewData(res.data);
            setImportStep('preview');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to parse CSV');
        } finally {
            setPreviewing(false);
        }
    };

    // Import confirm handler — step 2: actually import the data
    const handleImport = async () => {
        if (!importFile) return;
        setImporting(true);
        try {
            const formData = new FormData();
            formData.append('file', importFile);
            formData.append('list_name', importListName || 'Imported List');

            const res = await axios.post(`${API}/admin/target-accounts/import`, formData, {
                headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
            });

            toast.success(`Imported ${res.data.imported} companies${res.data.assigned ? ` (${res.data.assigned} assigned to reps)` : ''}${res.data.skipped ? ` (${res.data.skipped} skipped)` : ''}`);
            setShowImportModal(false);
            setImportFile(null);
            setImportListName('');
            setImportStep('upload');
            setPreviewData(null);
            setPreviewSearch('');
            loadData();
            loadDashboard();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Import failed');
        } finally {
            setImporting(false);
        }
    };

    // Reset import modal on close
    const closeImportModal = (open) => {
        setShowImportModal(open);
        if (!open) {
            setImportStep('upload');
            setPreviewData(null);
            setPreviewSearch('');
            setImportFile(null);
            setImportListName('');
        }
    };

    // Assign handler
    const handleAssign = async () => {
        if (!assignToUserId) {
            toast.error('Please select a sales rep');
            return;
        }
        setAssigning(true);
        try {
            const res = await axios.put(`${API}/admin/target-accounts/assign`, {
                target_account_ids: selectedIds,
                assign_to_user_id: assignToUserId,
                enroll_in_sequence_id: enrollCampaignId || null
            }, { headers: getAuthHeader() });
            
            toast.success(`Assigned ${res.data.assigned} companies to ${res.data.assigned_to}`);
            setShowAssignModal(false);
            setSelectedIds([]);
            setAssignToUserId('');
            setEnrollCampaignId('');
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Assignment failed');
        } finally {
            setAssigning(false);
        }
    };

    // Convert handler
    const handleConvert = async (targetId) => {
        try {
            const res = await axios.post(`${API}/admin/target-accounts/${targetId}/convert`, {}, {
                headers: getAuthHeader()
            });
            toast.success(`Converted "${res.data.account_name}" to CRM company`);
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Conversion failed');
        }
    };

    // Single delete handler
    const handleDelete = async (targetId) => {
        if (!window.confirm('Delete this target company?')) return;
        try {
            await axios.delete(`${API}/admin/target-accounts/${targetId}`, { headers: getAuthHeader() });
            toast.success('Target company deleted');
            loadData();
        } catch (err) {
            toast.error('Delete failed');
        }
    };

    // Bulk delete handler
    const handleBulkDelete = async () => {
        if (deleteConfirmText !== 'DELETE') return;
        setDeleting(true);
        try {
            const res = await axios.delete(`${API}/admin/target-accounts`, {
                headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
                data: selectedIds
            });
            toast.success(`Deleted ${res.data.deleted} target compan${res.data.deleted !== 1 ? 'ies' : 'y'}`);
            setShowDeleteModal(false);
            setDeleteConfirmText('');
            setSelectedIds([]);
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Bulk delete failed');
        } finally {
            setDeleting(false);
        }
    };

    // Restore handler
    const handleRestore = async (targetIds) => {
        setRestoring(true);
        try {
            const res = await axios.put(`${API}/admin/target-accounts/restore`, targetIds, { headers: getAuthHeader() });
            toast.success(`Restored ${res.data.restored} target compan${res.data.restored !== 1 ? 'ies' : 'y'}`);
            setSelectedIds([]);
            loadData();
        } catch (err) {
            toast.error('Restore failed');
        } finally {
            setRestoring(false);
        }
    };

    // Get initials for avatar
    const getInitials = (name) => {
        if (!name) return '??';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    };

    // Avatar colors
    const avatarColors = [
        'bg-blue-500', 'bg-purple-500', 'bg-pink-500', 'bg-orange-500', 'bg-green-500',
        'bg-stone-1000', 'bg-teal-500', 'bg-red-500'
    ];

    const getAvatarColor = (index) => avatarColors[index % avatarColors.length];

    // Status badge
    const StatusBadge = ({ status }) => {
        const styles = {
            target: 'bg-blue-100 text-blue-700',
            assigned: 'bg-amber-100 text-amber-700',
            converted: 'bg-green-100 text-green-700'
        };
        return (
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${styles[status] || 'bg-stone-100 text-stone-600'}`}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
        );
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen bg-stone-50">
                <div className="w-10 h-10 border-4 border-stone-700 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-stone-50 p-6" data-testid="list-management-page">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        List Management
                    </h1>
                    <p className="text-sm text-stone-500 mt-1">
                        {activeTab === 'staging' ? 'Import, review and commit contacts & companies to CRM' : 'Allocate target companies to salespeople'}
                    </p>
                </div>
                {activeTab !== 'staging' && (
                    <div className="flex items-center gap-3">
                        <Button
                            variant="outline"
                            onClick={() => setShowImportModal(true)}
                            data-testid="import-csv-btn"
                        >
                            <Upload className="w-4 h-4 mr-2" />
                            Import CSV
                        </Button>
                        {selectedIds.length > 0 && activeTab !== 'deleted' && (
                            <Button
                                variant="outline"
                                className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                                onClick={() => setShowDeleteModal(true)}
                                data-testid="bulk-delete-btn"
                            >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete ({selectedIds.length})
                            </Button>
                        )}
                        {selectedIds.length > 0 && activeTab === 'deleted' && (
                            <Button
                                variant="outline"
                                className="border-green-200 text-green-600 hover:bg-green-50 hover:text-green-700"
                                onClick={() => handleRestore(selectedIds)}
                                disabled={restoring}
                                data-testid="bulk-restore-btn"
                            >
                                <RotateCcw className="w-4 h-4 mr-2" />
                                {restoring ? 'Restoring...' : `Restore (${selectedIds.length})`}
                            </Button>
                        )}
                        <Button
                            variant="outline"
                            onClick={() => setShowEnrichModal(true)}
                            disabled={selectedIds.length === 0}
                        >
                            <Sparkles className="w-4 h-4 mr-2" />
                            Enrich ({selectedIds.length})
                        </Button>
                        <Button
                            onClick={() => setShowAISuggestions(true)}
                            disabled={selectedIds.length === 0}
                            className="bg-stone-900 hover:bg-stone-800"
                            data-testid="allocate-btn"
                        >
                            <UserPlus className="w-4 h-4 mr-2" />
                            Allocate ({selectedIds.length})
                        </Button>
                    </div>
                )}
            </div>

            {/* Dashboard Metrics — legacy target accounts (hidden on staging tab) */}
            {activeTab !== 'staging' && !dashboardLoading && dashboard && (
                <div className="mb-6 space-y-4">
                    {/* KPI Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                        {[
                            { key: 'total', label: 'Total Companies', value: dashboard.metrics.total, icon: Building2, iconCls: 'text-stone-500', activeBorder: 'border-stone-400 ring-2 ring-stone-100', badgeCls: 'bg-stone-100 text-stone-700', trend: dashboard.trends.new_added },
                            { key: 'target', label: 'Unallocated', value: dashboard.metrics.target, icon: Target, iconCls: 'text-blue-500', activeBorder: 'border-blue-400 ring-2 ring-blue-100', badgeCls: 'bg-blue-100 text-blue-700' },
                            { key: 'assigned', label: 'Assigned', value: dashboard.metrics.assigned, icon: UserPlus, iconCls: 'text-amber-500', activeBorder: 'border-amber-400 ring-2 ring-amber-100', badgeCls: 'bg-amber-100 text-amber-700', trend: dashboard.trends.assigned },
                            { key: 'converted', label: 'Converted', value: dashboard.metrics.converted, icon: CheckCircle2, iconCls: 'text-green-500', activeBorder: 'border-green-400 ring-2 ring-green-100', badgeCls: 'bg-green-100 text-green-700', trend: dashboard.trends.converted },
                            { key: 'contacts', label: 'Total Contacts', value: dashboard.metrics.total_contacts, icon: Users, iconCls: 'text-purple-500', activeBorder: 'border-purple-400 ring-2 ring-purple-100', badgeCls: 'bg-purple-100 text-purple-700', navigateTo: '/contacts' },
                            { key: 'rate', label: 'Conversion Rate', value: `${dashboard.metrics.conversion_rate}%`, icon: BarChart3, iconCls: 'text-emerald-500', activeBorder: '', badgeCls: '', noClick: true },
                        ].map(metric => (
                            <button
                                key={metric.key}
                                onClick={() => metric.navigateTo ? navigate(metric.navigateTo) : !metric.noClick && handleMetricClick(metric.key)}
                                className={`bg-white rounded-xl border p-4 text-left transition-all ${
                                    activeMetricFilter === metric.key
                                        ? `${metric.activeBorder} shadow-sm`
                                        : 'border-stone-200 hover:border-stone-300 hover:shadow-sm'
                                } ${metric.noClick ? 'cursor-default' : 'cursor-pointer'} ${metric.navigateTo ? 'cursor-pointer' : ''}`}
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <metric.icon className={`w-4 h-4 ${metric.iconCls}`} />
                                    {activeMetricFilter === metric.key && (
                                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${metric.badgeCls} font-medium`}>
                                            Filtered
                                        </span>
                                    )}
                                </div>
                                <p className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    {typeof metric.value === 'number' ? metric.value.toLocaleString() : metric.value}
                                </p>
                                <p className="text-xs text-stone-500 mt-0.5">{metric.label}</p>
                                {metric.trend && (
                                    <div className={`flex items-center gap-1 mt-2 text-xs ${
                                        metric.trend.change >= 0 ? 'text-green-600' : 'text-red-500'
                                    }`}>
                                        {metric.trend.change >= 0 ? (
                                            <TrendingUp className="w-3 h-3" />
                                        ) : (
                                            <TrendingDown className="w-3 h-3" />
                                        )}
                                        <span className="font-medium">
                                            {metric.trend.change >= 0 ? '+' : ''}{metric.trend.change}%
                                        </span>
                                        <span className="text-stone-400">vs prev 30d</span>
                                    </div>
                                )}
                            </button>
                        ))}
                    </div>

                    {/* Breakdown Toggle Buttons */}
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setShowBreakdown(showBreakdown === 'region' ? null : 'region')}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                showBreakdown === 'region' ? 'bg-stone-900 text-white' : 'bg-white border border-stone-200 text-stone-600 hover:bg-stone-50'
                            }`}
                        >
                            <Globe className="w-3 h-3" />
                            By Region
                            {showBreakdown === 'region' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                        </button>
                        <button
                            onClick={() => setShowBreakdown(showBreakdown === 'industry' ? null : 'industry')}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                showBreakdown === 'industry' ? 'bg-stone-900 text-white' : 'bg-white border border-stone-200 text-stone-600 hover:bg-stone-50'
                            }`}
                        >
                            <BarChart3 className="w-3 h-3" />
                            By Industry
                            {showBreakdown === 'industry' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                        </button>
                        {activeMetricFilter && (
                            <button
                                onClick={() => { setActiveMetricFilter(null); setStatusFilter(''); }}
                                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-amber-50 border border-amber-200 text-amber-700 hover:bg-amber-100"
                            >
                                <X className="w-3 h-3" />
                                Clear Filter
                            </button>
                        )}
                    </div>

                    {/* Region Breakdown */}
                    {showBreakdown === 'region' && dashboard.by_region?.length > 0 && (
                        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
                            <div className="px-4 py-3 border-b border-stone-100 flex items-center gap-2">
                                <Globe className="w-4 h-4 text-stone-500" />
                                <h3 className="text-sm font-semibold text-stone-800">Companies by Region</h3>
                            </div>
                            <div className="p-4">
                                <div className="space-y-3">
                                    {dashboard.by_region.map(r => {
                                        const pct = dashboard.metrics.total > 0 ? (r.total / dashboard.metrics.total * 100) : 0;
                                        return (
                                            <div key={r.region} className="group">
                                                <div className="flex items-center justify-between mb-1">
                                                    <button
                                                        onClick={() => { setRegionFilter(r.region === 'Unknown' ? '' : r.region); setActiveTab('targets'); }}
                                                        className="text-sm font-medium text-stone-700 hover:text-stone-900 hover:underline"
                                                    >
                                                        {r.region}
                                                    </button>
                                                    <div className="flex items-center gap-3 text-xs">
                                                        <span className="text-blue-600">{r.target} target</span>
                                                        <span className="text-amber-600">{r.assigned} assigned</span>
                                                        <span className="text-green-600">{r.converted} converted</span>
                                                        <span className="font-semibold text-stone-700">{r.total}</span>
                                                    </div>
                                                </div>
                                                <div className="h-2 bg-stone-100 rounded-full overflow-hidden flex">
                                                    {r.target > 0 && <div className="bg-blue-400 h-full" style={{ width: `${r.target / r.total * pct}%`, minWidth: r.target > 0 ? '2px' : 0 }} />}
                                                    {r.assigned > 0 && <div className="bg-amber-400 h-full" style={{ width: `${r.assigned / r.total * pct}%`, minWidth: r.assigned > 0 ? '2px' : 0 }} />}
                                                    {r.converted > 0 && <div className="bg-green-400 h-full" style={{ width: `${r.converted / r.total * pct}%`, minWidth: r.converted > 0 ? '2px' : 0 }} />}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Industry Breakdown */}
                    {showBreakdown === 'industry' && dashboard.by_industry?.length > 0 && (
                        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
                            <div className="px-4 py-3 border-b border-stone-100 flex items-center gap-2">
                                <BarChart3 className="w-4 h-4 text-stone-500" />
                                <h3 className="text-sm font-semibold text-stone-800">Companies by Industry</h3>
                            </div>
                            <div className="p-4">
                                <div className="space-y-3">
                                    {dashboard.by_industry.map(ind => {
                                        const pct = dashboard.metrics.total > 0 ? (ind.total / dashboard.metrics.total * 100) : 0;
                                        return (
                                            <div key={ind.industry} className="group">
                                                <div className="flex items-center justify-between mb-1">
                                                    <span className="text-sm font-medium text-stone-700">{ind.industry}</span>
                                                    <div className="flex items-center gap-3 text-xs">
                                                        <span className="text-blue-600">{ind.target} target</span>
                                                        <span className="text-amber-600">{ind.assigned} assigned</span>
                                                        <span className="text-green-600">{ind.converted} converted</span>
                                                        <span className="font-semibold text-stone-700">{ind.total}</span>
                                                    </div>
                                                </div>
                                                <div className="h-2 bg-stone-100 rounded-full overflow-hidden flex">
                                                    {ind.target > 0 && <div className="bg-blue-400 h-full" style={{ width: `${ind.target / ind.total * pct}%`, minWidth: ind.target > 0 ? '2px' : 0 }} />}
                                                    {ind.assigned > 0 && <div className="bg-amber-400 h-full" style={{ width: `${ind.assigned / ind.total * pct}%`, minWidth: ind.assigned > 0 ? '2px' : 0 }} />}
                                                    {ind.converted > 0 && <div className="bg-green-400 h-full" style={{ width: `${ind.converted / ind.total * pct}%`, minWidth: ind.converted > 0 ? '2px' : 0 }} />}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Tabs */}
            <div className="flex items-center gap-1 mb-4">
                <button onClick={() => { setActiveTab('staging'); setSelectedIds([]); }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'staging' ? 'bg-stone-900 text-white' : 'text-stone-600 hover:bg-stone-100'}`}>
                    <FileSpreadsheet className="w-4 h-4 inline mr-1.5" />Staging Area
                    {stagingImports.length > 0 && (
                        <span className="ml-1.5 text-xs bg-blue-500 text-white rounded-full px-1.5 py-0.5">
                            {stagingImports.reduce((s, i) => s + (i.pending_count || 0), 0)}
                        </span>
                    )}
                </button>
                <button onClick={() => { setActiveTab('targets'); setSelectedIds([]); }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'targets' ? 'bg-stone-900 text-white' : 'text-stone-600 hover:bg-stone-100'}`}>
                    <Building2 className="w-4 h-4 inline mr-1.5" />Target Companies
                </button>
                <button onClick={() => { setActiveTab('history'); loadImportHistory(); }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'history' ? 'bg-stone-900 text-white' : 'text-stone-600 hover:bg-stone-100'}`}>
                    <History className="w-4 h-4 inline mr-1.5" />Import History
                </button>
                <button onClick={() => { setActiveTab('deleted'); setSelectedIds([]); }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'deleted' ? 'bg-red-600 text-white' : 'text-stone-600 hover:bg-stone-100'}`}>
                    <Trash2 className="w-4 h-4 inline mr-1.5" />Deleted
                </button>
            </div>

            {/* ══ STAGING AREA TAB ══ */}
            {activeTab === 'staging' && (
                <div className="space-y-4">
                    {/* Staging header bar */}
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <Button onClick={() => setShowImportWizard(true)} className="bg-stone-900 hover:bg-stone-800">
                                <FileSpreadsheet className="w-4 h-4 mr-2" />Import Data
                            </Button>
                        </div>
                        <div className="flex items-center gap-2">
                            {stagingImports.length > 0 && (
                                <Button
                                    onClick={handleCommitPreview}
                                    className="bg-green-700 hover:bg-green-800 text-white"
                                >
                                    <CheckCircle2 className="w-4 h-4 mr-2" />
                                    {stagingSelectedIds.length > 0 ? `Commit ${stagingSelectedIds.length} Selected` : 'Commit to CRM'}
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* Dashboard with geographic filters + clickable cards */}
                    <StagingDashboard
                        dashboard={stagingDashboard}
                        loading={stagingDashboardLoading}
                        onFilterChange={(f) => { setStagingGeoFilters(f); }}
                        activeFilters={stagingGeoFilters}
                        onCardClick={handleCardClick}
                        activeView={stagingView}
                    />

                    {/* Rep balance cards */}
                    {stagingDashboard?.reps?.length > 0 && (
                        <StagingRepBalancer
                            reps={stagingDashboard.reps}
                            onRepClick={(uid) => setActiveRepFilter(prev => prev === uid ? '' : uid)}
                            activeRepId={activeRepFilter}
                        />
                    )}

                    {/* ── View based on card click ── */}

                    {/* DEFAULT: Staging table with import tabs */}
                    {stagingView === 'staging' && (
                        <>
                            {stagingImports.length > 0 && (
                                <StagingImportTabs
                                    imports={stagingImports}
                                    activeImportId={activeStagingImport}
                                    onSelect={setActiveStagingImport}
                                    onDelete={handleDeleteStagingImport}
                                />
                            )}

                            {stagingImports.length > 0 ? (
                                <StagingTable
                                    importId={activeStagingImport}
                                    getAuthHeader={getAuthHeader}
                                    salesReps={salesRepStats}
                                    onSelectionChange={setStagingSelectedIds}
                                    onDataChange={() => { loadStagingImports(); loadStagingDashboard(); }}
                                    geoFilters={stagingGeoFilters}
                                    repFilter={activeRepFilter}
                                />
                            ) : (
                                <div className="text-center py-16 border border-dashed border-stone-300 rounded-xl">
                                    <FileSpreadsheet className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                                    <h3 className="text-lg font-semibold text-stone-700 mb-1">No staged data</h3>
                                    <p className="text-sm text-stone-500 mb-1">Import data or upload a CSV to start staging contacts and companies for review</p>
                                    <p className="text-sm text-stone-400 mb-4">Data does not go into the CRM until reviewed and committed</p>
                                    <Button onClick={() => setShowImportWizard(true)} className="bg-stone-900 hover:bg-stone-800">
                                        <FileSpreadsheet className="w-4 h-4 mr-2" />Import Data
                                    </Button>
                                </div>
                            )}
                        </>
                    )}

                    {/* PENDING CONTACTS / UNASSIGNED: filtered staging table */}
                    {(stagingView === 'pending-contacts' || stagingView === 'unassigned') && (
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <h3 className="text-sm font-semibold text-stone-700">
                                    {stagingView === 'unassigned' ? 'Unassigned Staging Records' : 'Pending Contacts (Staging)'}
                                </h3>
                                <Button variant="outline" size="sm" onClick={() => setStagingView('staging')}>
                                    <X className="w-3.5 h-3.5 mr-1" />Back to Staging
                                </Button>
                            </div>
                            <StagingTable
                                importId={activeStagingImport}
                                getAuthHeader={getAuthHeader}
                                salesReps={salesRepStats}
                                onSelectionChange={setStagingSelectedIds}
                                onDataChange={() => { loadStagingImports(); loadStagingDashboard(); }}
                                geoFilters={stagingGeoFilters}
                                repFilter={stagingView === 'unassigned' ? 'unassigned' : activeRepFilter}
                            />
                        </div>
                    )}

                    {/* PENDING COMPANIES: grouped company table */}
                    {stagingView === 'pending-companies' && (
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <h3 className="text-sm font-semibold text-stone-700">Pending Companies (Staging)</h3>
                                <div className="flex items-center gap-2">
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                                        <Input placeholder="Search companies..." value={viewSearch} onChange={e => setViewSearch(e.target.value)} className="pl-9 w-64" />
                                    </div>
                                    <Button variant="outline" size="sm" onClick={() => setStagingView('staging')}>
                                        <X className="w-3.5 h-3.5 mr-1" />Back
                                    </Button>
                                </div>
                            </div>
                            {viewLoading ? (
                                <div className="flex items-center justify-center py-12 text-stone-400">
                                    <div className="w-5 h-5 border-2 border-stone-300 border-t-transparent rounded-full animate-spin mr-2" />Loading...
                                </div>
                            ) : (
                                <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
                                    <table className="w-full text-sm">
                                        <thead className="bg-stone-50 border-b border-stone-100">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Company</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Size</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">City</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">State</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Country</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Website</th>
                                                <th className="px-4 py-3 text-center text-xs font-medium text-stone-500 uppercase">Contacts</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Assigned To</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-stone-100">
                                            {pendingCompanies.data.length === 0 ? (
                                                <tr><td colSpan={8} className="px-4 py-8 text-center text-stone-400">No pending companies</td></tr>
                                            ) : pendingCompanies.data.map((co, idx) => (
                                                <tr key={idx} className="hover:bg-stone-50">
                                                    <td className="px-4 py-3 font-medium text-stone-800">{co.company_name || '—'}</td>
                                                    <td className="px-4 py-3 text-stone-600">{co.company_size || '—'}</td>
                                                    <td className="px-4 py-3 text-stone-600">{co.company_city || '—'}</td>
                                                    <td className="px-4 py-3 text-stone-600">{co.company_state || '—'}</td>
                                                    <td className="px-4 py-3 text-stone-600">{co.company_country || '—'}</td>
                                                    <td className="px-4 py-3 text-stone-600">
                                                        {co.company_website ? (
                                                            <a href={co.company_website.startsWith('http') ? co.company_website : `https://${co.company_website}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate block max-w-[150px]">{co.company_website}</a>
                                                        ) : '—'}
                                                    </td>
                                                    <td className="px-4 py-3 text-center">
                                                        <span className="inline-flex items-center gap-1 text-stone-600"><Users className="w-3 h-3" />{co.contacts_count}</span>
                                                    </td>
                                                    <td className="px-4 py-3 text-stone-600">{co.assigned_to_name || <span className="text-amber-500">Unassigned</span>}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {pendingCompanies.total > 0 && (
                                        <div className="px-4 py-2 border-t border-stone-100 text-xs text-stone-400">{pendingCompanies.total} companies</div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* CRM COMPANIES: live CRM accounts table */}
                    {stagingView === 'crm-companies' && (
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <h3 className="text-sm font-semibold text-stone-700">CRM Companies</h3>
                                <div className="flex items-center gap-2">
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                                        <Input placeholder="Search companies..." value={viewSearch} onChange={e => setViewSearch(e.target.value)} className="pl-9 w-64" />
                                    </div>
                                    <Button variant="outline" size="sm" onClick={() => setStagingView('staging')}>
                                        <X className="w-3.5 h-3.5 mr-1" />Back
                                    </Button>
                                </div>
                            </div>
                            {viewLoading ? (
                                <div className="flex items-center justify-center py-12 text-stone-400">
                                    <div className="w-5 h-5 border-2 border-stone-300 border-t-transparent rounded-full animate-spin mr-2" />Loading...
                                </div>
                            ) : (
                                <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
                                    <table className="w-full text-sm">
                                        <thead className="bg-stone-50 border-b border-stone-100">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Company</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Industry</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">City</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Country</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Website</th>
                                                <th className="px-4 py-3 text-center text-xs font-medium text-stone-500 uppercase">Contacts</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Owner</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-stone-100">
                                            {crmCompanies.data.length === 0 ? (
                                                <tr><td colSpan={8} className="px-4 py-8 text-center text-stone-400">No companies in CRM</td></tr>
                                            ) : crmCompanies.data.map(co => (
                                                <tr key={co.account_id} className="hover:bg-stone-50 cursor-pointer" onClick={() => navigate(`/companies/${co.account_id}`)}>
                                                    <td className="px-4 py-3 font-medium text-stone-800">{co.name}</td>
                                                    <td className="px-4 py-3 text-stone-600">{co.industry || '—'}</td>
                                                    <td className="px-4 py-3 text-stone-600">{co.city || '—'}</td>
                                                    <td className="px-4 py-3 text-stone-600">{co.country || '—'}</td>
                                                    <td className="px-4 py-3 text-stone-600">
                                                        {co.website ? <span className="truncate block max-w-[150px]">{co.domain || co.website}</span> : '—'}
                                                    </td>
                                                    <td className="px-4 py-3 text-center"><span className="inline-flex items-center gap-1"><Users className="w-3 h-3" />{co.contact_count || 0}</span></td>
                                                    <td className="px-4 py-3 text-stone-600">{co.owner_name || '—'}</td>
                                                    <td className="px-4 py-3">
                                                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${co.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-600'}`}>
                                                            {co.status || 'active'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {crmCompanies.total > 0 && (
                                        <div className="px-4 py-2 border-t border-stone-100 text-xs text-stone-400">{crmCompanies.total} companies in CRM</div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* CRM CONTACTS: live CRM contacts table */}
                    {stagingView === 'crm-contacts' && (
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <h3 className="text-sm font-semibold text-stone-700">CRM Contacts</h3>
                                <div className="flex items-center gap-2">
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                                        <Input placeholder="Search contacts..." value={viewSearch} onChange={e => setViewSearch(e.target.value)} className="pl-9 w-64" />
                                    </div>
                                    <Button variant="outline" size="sm" onClick={() => setStagingView('staging')}>
                                        <X className="w-3.5 h-3.5 mr-1" />Back
                                    </Button>
                                </div>
                            </div>
                            {viewLoading ? (
                                <div className="flex items-center justify-center py-12 text-stone-400">
                                    <div className="w-5 h-5 border-2 border-stone-300 border-t-transparent rounded-full animate-spin mr-2" />Loading...
                                </div>
                            ) : (
                                <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
                                    <table className="w-full text-sm">
                                        <thead className="bg-stone-50 border-b border-stone-100">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Name</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Email</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Title</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Company</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Seniority</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Owner</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-stone-100">
                                            {crmContacts.data.length === 0 ? (
                                                <tr><td colSpan={6} className="px-4 py-8 text-center text-stone-400">No contacts in CRM</td></tr>
                                            ) : crmContacts.data.map(ct => (
                                                <tr key={ct.contact_id} className="hover:bg-stone-50 cursor-pointer" onClick={() => navigate(`/contacts/${ct.contact_id}`)}>
                                                    <td className="px-4 py-3 font-medium text-stone-800">{ct.full_name || `${ct.first_name || ''} ${ct.last_name || ''}`.trim() || '—'}</td>
                                                    <td className="px-4 py-3 text-stone-600">{ct.email || '—'}</td>
                                                    <td className="px-4 py-3 text-stone-600">{ct.title || '—'}</td>
                                                    <td className="px-4 py-3 text-stone-600">{ct.account_name || '—'}</td>
                                                    <td className="px-4 py-3">
                                                        {ct.seniority ? <span className="px-2 py-0.5 rounded text-xs bg-stone-100 text-stone-600">{ct.seniority}</span> : '—'}
                                                    </td>
                                                    <td className="px-4 py-3 text-stone-600">{ct.owner_name || '—'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {crmContacts.total > 0 && (
                                        <div className="px-4 py-2 border-t border-stone-100 text-xs text-stone-400">{crmContacts.total} contacts in CRM</div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Staging upload modal */}
                    <StagingUploadModal
                        open={showStagingUpload}
                        onClose={() => setShowStagingUpload(false)}
                        getAuthHeader={getAuthHeader}
                        onUploadComplete={() => { loadStagingImports(); loadStagingDashboard(); }}
                    />

                    {/* Commit confirmation modal */}
                    <StagingCommitModal
                        open={showCommitModal}
                        onClose={() => setShowCommitModal(false)}
                        commitPreview={commitPreview}
                        onCommit={handleCommit}
                        committing={committing}
                        selectedCount={stagingSelectedIds.length}
                    />

                    {/* Import Wizard modal */}
                    <Dialog open={showImportWizard} onOpenChange={setShowImportWizard}>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-2">
                                    <FileSpreadsheet className="w-5 h-5 text-stone-700" />
                                    Data Import Wizard
                                </DialogTitle>
                                <DialogDescription>
                                    Import contacts and companies to the staging area with field mapping and duplicate handling.
                                </DialogDescription>
                            </DialogHeader>
                            <ImportWizard onGoToStaging={(importId) => {
                                setShowImportWizard(false);
                                setActiveTab('staging');
                                if (importId) setActiveStagingImport(importId);
                                loadStagingImports();
                                loadStagingDashboard();
                            }} />
                        </DialogContent>
                    </Dialog>
                </div>
            )}

            {activeTab === 'targets' && (<>
            {/* Filters Row */}
            <div className="flex items-center gap-3 mb-6">
                <div className="relative flex-1 max-w-xs">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                    <Input
                        placeholder="Search companies..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        className="pl-9"
                        data-testid="search-input"
                    />
                </div>
                <Select value={regionFilter || "all"} onValueChange={v => setRegionFilter(v === "all" ? "" : v)}>
                    <SelectTrigger className="w-36" data-testid="region-filter">
                        <SelectValue placeholder="All Regions" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Regions</SelectItem>
                        {filters.regions.map(r => (
                            <SelectItem key={r} value={r}>{r}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select value={countryFilter || "all"} onValueChange={v => setCountryFilter(v === "all" ? "" : v)}>
                    <SelectTrigger className="w-36" data-testid="country-filter">
                        <SelectValue placeholder="All Countries" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Countries</SelectItem>
                        {filters.countries.map(c => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select value={stateFilter || "all"} onValueChange={v => setStateFilter(v === "all" ? "" : v)}>
                    <SelectTrigger className="w-36" data-testid="state-filter">
                        <SelectValue placeholder="All States" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All States</SelectItem>
                        {filters.states.map(s => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select value={statusFilter || "all"} onValueChange={v => setStatusFilter(v === "all" ? "" : v)}>
                    <SelectTrigger className="w-32" data-testid="status-filter">
                        <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="target">Target</SelectItem>
                        <SelectItem value="assigned">Assigned</SelectItem>
                        <SelectItem value="converted">Converted</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Sales Rep Cards */}
            <div className="flex gap-3 mb-6 overflow-x-auto pb-2">
                {salesRepStats.map((rep, idx) => (
                    <div
                        key={rep.user_id}
                        className="flex-shrink-0 bg-white rounded-xl border border-stone-200 p-4 min-w-[200px] hover:border-stone-300 transition-colors"
                        data-testid={`rep-card-${rep.user_id}`}
                    >
                        <div className="flex items-center gap-3 mb-3">
                            <div className={`w-10 h-10 rounded-full ${getAvatarColor(idx)} flex items-center justify-center text-white font-semibold text-sm`}>
                                {getInitials(rep.name)}
                            </div>
                            <div className="min-w-0">
                                <p className="font-semibold text-stone-800 text-sm truncate">{rep.name}</p>
                                <p className="text-xs text-stone-400 truncate">{rep.email}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4 text-sm">
                            <div>
                                <span className="text-stone-400">Companies:</span>
                                <span className="ml-1 font-bold text-stone-800">{rep.accounts_count}</span>
                            </div>
                            <div>
                                <span className="text-stone-400">Contacts:</span>
                                <span className="ml-1 font-bold text-stone-800">{rep.contacts_count}</span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Target Companies Table */}
            <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-stone-100 flex items-center justify-between">
                    <h3 className="font-semibold text-stone-800">Target Companies</h3>
                    <span className="text-sm text-stone-400">{accounts.length} companies</span>
                </div>
                
                {accounts.length === 0 ? (
                    <div className="text-center py-12">
                        <FileSpreadsheet className="w-12 h-12 mx-auto text-stone-300 mb-3" />
                        <p className="text-stone-500 mb-2">No target companies found</p>
                        <p className="text-sm text-stone-400">Import a CSV file to get started</p>
                        <Button
                            variant="outline"
                            className="mt-4"
                            onClick={() => setShowImportModal(true)}
                        >
                            <Upload className="w-4 h-4 mr-2" />
                            Import CSV
                        </Button>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-stone-50 border-b border-stone-100">
                                <tr>
                                    <th className="px-4 py-3 text-left">
                                        <Checkbox
                                            checked={selectedIds.length === accounts.length && accounts.length > 0}
                                            onCheckedChange={toggleSelectAll}
                                            data-testid="select-all-checkbox"
                                        />
                                    </th>
                                    {LIST_COLUMNS.map(col => (
                                        <SortableHeader
                                            key={col.key}
                                            label={col.label}
                                            columnKey={col.key}
                                            sortColumn={sortColumn}
                                            sortDirection={sortDirection}
                                            onSort={handleSort}
                                            filterValue={columnFilters[col.key]}
                                            onFilter={setColumnFilter}
                                            allData={accounts}
                                        />
                                    ))}
                                    <th className="px-4 py-3 text-left font-medium text-stone-600 text-xs uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-stone-100">
                                {pagedAccounts.map(account => (
                                    <tr
                                        key={account.target_account_id}
                                        className={`hover:bg-stone-50 transition-colors ${selectedIds.includes(account.target_account_id) ? 'bg-stone-100' : ''}`}
                                        data-testid={`account-row-${account.target_account_id}`}
                                    >
                                        <td className="px-4 py-3">
                                            <Checkbox
                                                checked={selectedIds.includes(account.target_account_id)}
                                                onCheckedChange={() => toggleSelect(account.target_account_id)}
                                            />
                                        </td>
                                        <td className="px-4 py-3">
                                            <div>
                                                <p className="font-medium text-stone-800">{account.company_name}</p>
                                                {account.revenue_range && (
                                                    <p className="text-xs text-stone-400">{account.revenue_range}</p>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-stone-600">{account.industry || '-'}</td>
                                        <td className="px-4 py-3 text-stone-600">{account.employees || '-'}</td>
                                        <td className="px-4 py-3 text-stone-600">{account.investor || '-'}</td>
                                        <td className="px-4 py-3">
                                            {account.investor_type ? (
                                                <span className="px-2 py-0.5 rounded bg-stone-100 text-stone-600 text-xs">
                                                    {account.investor_type}
                                                </span>
                                            ) : '-'}
                                        </td>
                                        <td className="px-4 py-3 text-stone-600">{account.city || '-'}</td>
                                        <td className="px-4 py-3 text-stone-600">{account.state_province || '-'}</td>
                                        <td className="px-4 py-3 text-stone-600">{account.country || '-'}</td>
                                        <td className="px-4 py-3 text-stone-600">{account.region || '-'}</td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-1 text-stone-600">
                                                <Users className="w-3 h-3" />
                                                {account.contacts_count}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <StatusBadge status={account.status} />
                                        </td>
                                        <td className="px-4 py-3 text-stone-600">
                                            {account.assigned_to_name || 'Unassigned'}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-1">
                                                {account.status === 'assigned' && !account.converted_account_id && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-7 text-xs"
                                                        onClick={() => handleConvert(account.target_account_id)}
                                                        title="Convert to Company"
                                                    >
                                                        <ArrowRightCircle className="w-3 h-3" />
                                                    </Button>
                                                )}
                                                {account.status === 'converted' && (
                                                    <CheckCircle2 className="w-4 h-4 text-green-500" title="Converted" />
                                                )}
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-7 text-xs text-red-500 hover:text-red-600 hover:bg-red-50"
                                                    onClick={() => handleDelete(account.target_account_id)}
                                                >
                                                    <Trash2 className="w-3 h-3" />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <TablePagination
                            page={page}
                            totalPages={totalPages}
                            total={totalAccounts}
                            pageSize={pageSize}
                            onPageChange={setPage}
                            onPageSizeChange={setPageSize}
                        />
                    </div>
                )}
            </div>

            </>)}

            {/* Import CSV Modal — 2-step: Upload → Preview → Confirm */}
            <Dialog open={showImportModal} onOpenChange={closeImportModal}>
                <DialogContent className={importStep === 'preview' ? 'sm:max-w-5xl max-h-[90vh] flex flex-col' : 'sm:max-w-md'}>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FileSpreadsheet className="w-5 h-5 text-stone-700" />
                            {importStep === 'upload' ? 'Import Target Companies' : 'Review Import Data'}
                        </DialogTitle>
                        <DialogDescription>
                            {importStep === 'upload'
                                ? 'Upload a CSV file with target companies from PitchBook or other sources.'
                                : `${previewData?.valid_rows || 0} companies found in CSV. Review before importing.`}
                        </DialogDescription>
                    </DialogHeader>

                    {importStep === 'upload' && (
                        <>
                            <div className="space-y-4 py-4">
                                <div>
                                    <Label>List Name</Label>
                                    <Input
                                        value={importListName}
                                        onChange={e => setImportListName(e.target.value)}
                                        placeholder="e.g., PitchBook Q1 2026"
                                        className="mt-1"
                                        data-testid="import-list-name"
                                    />
                                </div>
                                <div>
                                    <Label>CSV File</Label>
                                    <div className="mt-1 border-2 border-dashed border-stone-200 rounded-lg p-6 text-center">
                                        <input
                                            type="file"
                                            accept=".csv"
                                            onChange={e => setImportFile(e.target.files?.[0] || null)}
                                            className="hidden"
                                            id="csv-upload"
                                            data-testid="csv-file-input"
                                        />
                                        <label htmlFor="csv-upload" className="cursor-pointer">
                                            <Upload className="w-8 h-8 mx-auto text-stone-400 mb-2" />
                                            {importFile ? (
                                                <p className="text-sm text-stone-700 font-medium">{importFile.name}</p>
                                            ) : (
                                                <p className="text-sm text-stone-500">Click to upload CSV file</p>
                                            )}
                                        </label>
                                    </div>
                                </div>
                                <div className="text-xs text-stone-400 space-y-1">
                                    <p><strong>Supported columns:</strong></p>
                                    <p>Company, Revenue, Industry, Employees, Investor, Investor Type, City, State, Country, Region, Contacts</p>
                                </div>
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => closeImportModal(false)}>Cancel</Button>
                                <Button
                                    onClick={handlePreview}
                                    disabled={previewing || !importFile}
                                    className="bg-stone-900 hover:bg-stone-800"
                                    data-testid="import-preview-btn"
                                >
                                    {previewing ? (
                                        <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />Parsing...</>
                                    ) : (
                                        <><Search className="w-4 h-4 mr-2" />Preview Data</>
                                    )}
                                </Button>
                            </DialogFooter>
                        </>
                    )}

                    {importStep === 'preview' && previewData && (
                        <>
                            {/* Summary stats */}
                            <div className="flex items-center gap-4 py-3 px-1">
                                <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                                    <span className="text-sm font-medium text-green-800">{previewData.valid_rows} valid</span>
                                </div>
                                {previewData.skipped_rows > 0 && (
                                    <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                                        <AlertTriangle className="w-4 h-4 text-amber-600" />
                                        <span className="text-sm font-medium text-amber-800">{previewData.skipped_rows} skipped (no company name)</span>
                                    </div>
                                )}
                                {previewData.unmatched_reps?.length > 0 && (
                                    <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                                        <AlertTriangle className="w-4 h-4 text-amber-600" />
                                        <span className="text-sm font-medium text-amber-800">{previewData.unmatched_reps.length} rep(s) not found: {previewData.unmatched_reps.join(', ')}</span>
                                    </div>
                                )}
                                <div className="flex items-center gap-2 text-xs text-stone-500 ml-auto">
                                    <span>Mapped: {Object.keys(previewData.mapped_columns || {}).join(', ')}</span>
                                </div>
                            </div>

                            {/* Search within preview */}
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                                <Input
                                    placeholder="Search preview data..."
                                    value={previewSearch}
                                    onChange={e => setPreviewSearch(e.target.value)}
                                    className="pl-9"
                                />
                            </div>

                            {/* Preview table */}
                            <div className="flex-1 overflow-auto border border-stone-200 rounded-lg min-h-0">
                                <table className="w-full text-sm">
                                    <thead className="bg-stone-50 border-b border-stone-200 sticky top-0 z-10">
                                        <tr>
                                            <th className="px-3 py-2 text-left text-xs font-semibold text-stone-500 w-10">#</th>
                                            <th className="px-3 py-2 text-left text-xs font-semibold text-stone-500">Company</th>
                                            <th className="px-3 py-2 text-left text-xs font-semibold text-stone-500">Industry</th>
                                            <th className="px-3 py-2 text-left text-xs font-semibold text-stone-500">Employees</th>
                                            <th className="px-3 py-2 text-left text-xs font-semibold text-stone-500">Investor</th>
                                            <th className="px-3 py-2 text-left text-xs font-semibold text-stone-500">Type</th>
                                            <th className="px-3 py-2 text-left text-xs font-semibold text-stone-500">City</th>
                                            <th className="px-3 py-2 text-left text-xs font-semibold text-stone-500">State</th>
                                            <th className="px-3 py-2 text-left text-xs font-semibold text-stone-500">Country</th>
                                            <th className="px-3 py-2 text-left text-xs font-semibold text-stone-500">Region</th>
                                            <th className="px-3 py-2 text-right text-xs font-semibold text-stone-500">Contacts</th>
                                            <th className="px-3 py-2 text-left text-xs font-semibold text-stone-500">Assigned To</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-stone-100">
                                        {(previewSearch
                                            ? previewData.rows.filter(r =>
                                                Object.values(r).some(v => v && String(v).toLowerCase().includes(previewSearch.toLowerCase()))
                                            )
                                            : previewData.rows
                                        ).map((row, idx) => (
                                            <tr key={idx} className="hover:bg-stone-50">
                                                <td className="px-3 py-2 text-xs text-stone-400">{idx + 1}</td>
                                                <td className="px-3 py-2 font-medium text-stone-800">{row.company_name}</td>
                                                <td className="px-3 py-2 text-stone-600">{row.industry || <span className="text-stone-300">—</span>}</td>
                                                <td className="px-3 py-2 text-stone-600">{row.employees || <span className="text-stone-300">—</span>}</td>
                                                <td className="px-3 py-2 text-stone-600">{row.investor || <span className="text-stone-300">—</span>}</td>
                                                <td className="px-3 py-2 text-stone-600">{row.investor_type || <span className="text-stone-300">—</span>}</td>
                                                <td className="px-3 py-2 text-stone-600">{row.city || <span className="text-stone-300">—</span>}</td>
                                                <td className="px-3 py-2 text-stone-600">{row.state_province || <span className="text-stone-300">—</span>}</td>
                                                <td className="px-3 py-2 text-stone-600">{row.country || <span className="text-stone-300">—</span>}</td>
                                                <td className="px-3 py-2 text-stone-600">{row.region || <span className="text-stone-300">—</span>}</td>
                                                <td className="px-3 py-2 text-right text-stone-600">{row.contacts_count}</td>
                                                <td className="px-3 py-2 text-stone-600">
                                                    {row.sales_rep_matched ? (
                                                        <span className="text-green-600 flex items-center gap-1">
                                                            <CheckCircle2 className="w-3 h-3" /> {row.sales_rep_matched_name}
                                                        </span>
                                                    ) : row.sales_rep_name || row.sales_rep_email ? (
                                                        <span className="text-amber-600 flex items-center gap-1">
                                                            <AlertTriangle className="w-3 h-3" /> {row.sales_rep_name || row.sales_rep_email} <span className="text-xs">(not found)</span>
                                                        </span>
                                                    ) : (
                                                        <span className="text-stone-300">—</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <DialogFooter className="flex items-center justify-between gap-2">
                                <Button
                                    variant="outline"
                                    onClick={() => { setImportStep('upload'); setPreviewData(null); setPreviewSearch(''); }}
                                >
                                    <ArrowRight className="w-4 h-4 mr-2 rotate-180" />
                                    Back
                                </Button>
                                <div className="flex items-center gap-2">
                                    <Button variant="outline" onClick={() => closeImportModal(false)}>Cancel</Button>
                                    <Button
                                        onClick={handleImport}
                                        disabled={importing || !previewData?.valid_rows}
                                        className="bg-stone-900 hover:bg-stone-800"
                                        data-testid="import-submit-btn"
                                    >
                                        {importing ? (
                                            <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />Importing...</>
                                        ) : (
                                            <>Import {previewData.valid_rows} Companies</>
                                        )}
                                    </Button>
                                </div>
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Import History Tab */}
            {activeTab === 'history' && (
                <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-stone-50 border-b border-stone-200">
                            <tr>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase">List Name</th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase">Date</th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase">Time of Import</th>
                                <th className="text-center px-4 py-3 text-xs font-semibold text-stone-500 uppercase">Companies</th>
                                <th className="text-center px-4 py-3 text-xs font-semibold text-stone-500 uppercase">Contacts</th>
                                <th className="text-center px-4 py-3 text-xs font-semibold text-stone-500 uppercase">Total Rows</th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase">Status</th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase">Created By</th>
                            </tr>
                        </thead>
                        <tbody>
                            {importHistory.length === 0 ? (
                                <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-stone-400">No import history</td></tr>
                            ) : importHistory.map(h => {
                                const importDate = h.created_at ? new Date(h.created_at) : null;
                                const dateStr = importDate ? importDate.toLocaleDateString() : 'Unknown';
                                const timeStr = importDate ? importDate.toLocaleTimeString() : 'Unknown';
                                return (
                                <tr key={h.import_id} className="border-b border-stone-100 hover:bg-stone-50">
                                    <td className="px-4 py-3 font-medium text-stone-900">{h.list_name}</td>
                                    <td className="px-4 py-3 text-stone-500">{dateStr}</td>
                                    <td className="px-4 py-3 text-stone-500">{timeStr}</td>
                                    <td className="px-4 py-3 text-center text-stone-700">
                                        {h.committed_accounts || h.company_count || h.committed_count || h.valid_rows || 0}
                                    </td>
                                    <td className="px-4 py-3 text-center text-stone-700">{h.committed_contacts || h.contact_count || '—'}</td>
                                    <td className="px-4 py-3 text-center text-stone-500">{h.total_rows || h.valid_rows || 0}</td>
                                    <td className="px-4 py-3">
                                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                            h.status === 'committed' ? 'bg-green-100 text-green-700' : 
                                            h.status === 'active' ? 'bg-blue-100 text-blue-700' : 
                                            'bg-stone-100 text-stone-600'
                                        }`}>
                                            {h.status === 'committed' ? 'Committed' : 
                                             h.status === 'active' ? 'In Staging' : 
                                             h.status.charAt(0).toUpperCase() + h.status.slice(1)}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-stone-500">{h.created_by_name || 'Unknown'}</td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Deleted Tab */}
            {activeTab === 'deleted' && (
                <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
                    <div className="px-4 py-3 border-b border-stone-100 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Trash2 className="w-4 h-4 text-red-500" />
                            <h3 className="font-semibold text-stone-800">Deleted Companies</h3>
                        </div>
                        <span className="text-sm text-stone-400">{accounts.length} deleted</span>
                    </div>
                    {accounts.length === 0 ? (
                        <div className="text-center py-12">
                            <CheckCircle2 className="w-12 h-12 mx-auto text-green-300 mb-3" />
                            <p className="text-stone-500 mb-2">No deleted companies</p>
                            <p className="text-sm text-stone-400">Deleted target companies will appear here for recovery</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-red-50/50 border-b border-stone-100">
                                    <tr>
                                        <th className="px-4 py-3 text-left">
                                            <Checkbox
                                                checked={selectedIds.length === accounts.length && accounts.length > 0}
                                                onCheckedChange={toggleSelectAll}
                                            />
                                        </th>
                                        <th className="px-4 py-3 text-left font-medium text-stone-600">Company</th>
                                        <th className="px-4 py-3 text-left font-medium text-stone-600">Industry</th>
                                        <th className="px-4 py-3 text-left font-medium text-stone-600">Status</th>
                                        <th className="px-4 py-3 text-left font-medium text-stone-600">Deleted By</th>
                                        <th className="px-4 py-3 text-left font-medium text-stone-600">Deleted At</th>
                                        <th className="px-4 py-3 text-left font-medium text-stone-600">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-stone-100">
                                    {accounts.map(account => (
                                        <tr key={account.target_account_id}
                                            className={`hover:bg-stone-50 transition-colors ${selectedIds.includes(account.target_account_id) ? 'bg-stone-100' : ''}`}>
                                            <td className="px-4 py-3">
                                                <Checkbox
                                                    checked={selectedIds.includes(account.target_account_id)}
                                                    onCheckedChange={() => toggleSelect(account.target_account_id)}
                                                />
                                            </td>
                                            <td className="px-4 py-3">
                                                <p className="font-medium text-stone-800">{account.company_name}</p>
                                                {account.region && <p className="text-xs text-stone-400">{account.region}</p>}
                                            </td>
                                            <td className="px-4 py-3 text-stone-600">{account.industry || '-'}</td>
                                            <td className="px-4 py-3"><StatusBadge status={account.status} /></td>
                                            <td className="px-4 py-3 text-stone-600">{account.deleted_by_name || 'Unknown'}</td>
                                            <td className="px-4 py-3 text-stone-500 text-xs">
                                                {account.deleted_at ? new Date(account.deleted_at).toLocaleString() : '-'}
                                            </td>
                                            <td className="px-4 py-3">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-7 text-xs text-green-600 hover:text-green-700 hover:bg-green-50"
                                                    onClick={() => handleRestore([account.target_account_id])}
                                                    disabled={restoring}
                                                >
                                                    <RotateCcw className="w-3 h-3 mr-1" />
                                                    Restore
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Bulk Delete Confirmation Modal */}
            <Dialog open={showDeleteModal} onOpenChange={(open) => { setShowDeleteModal(open); if (!open) setDeleteConfirmText(''); }}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <AlertTriangle className="w-5 h-5" />
                            Delete {selectedIds.length} Target Compan{selectedIds.length !== 1 ? 'ies' : 'y'}
                        </DialogTitle>
                        <DialogDescription>
                            This will soft-delete the selected companies. They can be restored from the Deleted tab.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="bg-red-50 border border-red-100 rounded-lg p-4">
                            <p className="text-sm text-red-800 font-medium mb-1">
                                You are about to delete {selectedIds.length} target compan{selectedIds.length !== 1 ? 'ies' : 'y'}.
                            </p>
                            <p className="text-xs text-red-600">
                                Deleted companies will be moved to the Deleted tab and can be restored later.
                            </p>
                        </div>
                        <div>
                            <Label className="text-sm text-stone-600">
                                Type <span className="font-bold text-stone-900">DELETE</span> to confirm
                            </Label>
                            <Input
                                value={deleteConfirmText}
                                onChange={e => setDeleteConfirmText(e.target.value)}
                                placeholder="Type DELETE to confirm"
                                className="mt-2"
                                autoFocus
                                data-testid="delete-confirm-input"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setShowDeleteModal(false); setDeleteConfirmText(''); }}>Cancel</Button>
                        <Button
                            onClick={handleBulkDelete}
                            disabled={deleting || deleteConfirmText !== 'DELETE'}
                            className="bg-red-600 hover:bg-red-700 text-white"
                            data-testid="confirm-delete-btn"
                        >
                            {deleting ? 'Deleting...' : `Delete ${selectedIds.length} Compan${selectedIds.length !== 1 ? 'ies' : 'y'}`}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* AI Assignment Suggestions Modal */}
            <Dialog open={showAISuggestions} onOpenChange={setShowAISuggestions}>
                <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Sparkles className="w-5 h-5 text-amber-500" />
                            Smart Allocation
                        </DialogTitle>
                        <DialogDescription>
                            AI recommends the best-fit sales rep for {selectedIds.length} selected compan{selectedIds.length !== 1 ? 'ies' : 'y'}.
                        </DialogDescription>
                    </DialogHeader>
                    <AssignmentSuggester
                        targetAccountIds={selectedIds}
                        onSelectRep={(targetId, userId, userName) => {
                            setAssignToUserId(userId);
                            setShowAISuggestions(false);
                            setShowAssignModal(true);
                        }}
                        onSkip={() => {
                            setShowAISuggestions(false);
                            setShowAssignModal(true);
                        }}
                    />
                </DialogContent>
            </Dialog>

            {/* Enrichment Modal */}
            <EnrichmentModal
                open={showEnrichModal}
                onOpenChange={setShowEnrichModal}
                recordType="account"
                recordIds={selectedIds}
                onComplete={loadData}
            />

            {/* Assign Modal */}
            <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <UserPlus className="w-5 h-5 text-stone-700" />
                            Allocate Companies
                        </DialogTitle>
                        <DialogDescription>
                            Assign {selectedIds.length} selected compan{selectedIds.length !== 1 ? 'ies' : 'y'} to a sales rep.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div>
                            <Label>Assign To</Label>
                            <Select value={assignToUserId} onValueChange={setAssignToUserId}>
                                <SelectTrigger className="mt-1" data-testid="assign-to-select">
                                    <SelectValue placeholder="Select sales rep" />
                                </SelectTrigger>
                                <SelectContent>
                                    {salesRepStats.map(rep => (
                                        <SelectItem key={rep.user_id} value={rep.user_id}>
                                            {rep.name} ({rep.accounts_count} companies)
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Enroll in Campaign (Optional)</Label>
                            <Select value={enrollCampaignId || "none"} onValueChange={v => setEnrollCampaignId(v === "none" ? "" : v)}>
                                <SelectTrigger className="mt-1" data-testid="enroll-campaign-select">
                                    <SelectValue placeholder="Don't enroll" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">Don't enroll</SelectItem>
                                    {campaigns.map(camp => (
                                        <SelectItem key={camp.campaign_id} value={camp.campaign_id}>
                                            {camp.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-stone-400 mt-1">
                                Optionally enroll these companies in a campaign
                            </p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowAssignModal(false)}>Cancel</Button>
                        <Button
                            onClick={handleAssign}
                            disabled={assigning || !assignToUserId}
                            className="bg-stone-900 hover:bg-stone-800"
                            data-testid="assign-submit-btn"
                        >
                            {assigning ? 'Assigning...' : `Allocate ${selectedIds.length} Compan${selectedIds.length !== 1 ? 'ies' : 'y'}`}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
